// Firebase configuration for BSR Robotics
// IMPORTANT: This file requires proper environment configuration
// Copy .env.example to .env and configure with your Firebase project details
//
// For demo mode:
// - Set VITE_DEMO_MODE=true to enable frontend data generation (NOT for production)
// - Configure Firebase project credentials in .env
//
// For production:
// - Set VITE_DEMO_MODE=false
// - Ensure all Firebase writes go through secured backend APIs
// - Configure proper Firebase security rules

import { initializeApp } from 'firebase/app';
import { getFirestore, enableNetwork, disableNetwork } from 'firebase/firestore';
import { getAuth, setPersistence, browserLocalPersistence, inMemoryPersistence } from 'firebase/auth';

// Validate required environment variables
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID'
];

const missingVars = requiredEnvVars.filter(varName => !import.meta.env[varName]);

if (missingVars.length > 0 && import.meta.env.PROD) {
  console.error('Missing required Firebase configuration:', missingVars.join(', '));
  console.error('Please configure your .env file with Firebase credentials');
}

// Firebase configuration object - NO HARDCODED VALUES
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL || ''
};

// Initialize Firebase only if we have required config.
// Realtime Database is intentionally NOT initialized — the app reads only from
// Firestore. Loading the RTDB SDK was causing background websocket failures
// (firebase:previous_websocket_failure in localStorage) that could entangle
// auth bootstrap.
let app, db, auth;

try {
  if (firebaseConfig.projectId) {
    app = initializeApp(firebaseConfig);
    db = getFirestore(app);
    auth = getAuth(app);

    // Force Auth onto localStorage instead of the SDK default
    // (indexedDBLocalPersistence). IndexedDB in some browsers can lock or
    // corrupt across sessions, which causes signInWithEmailAndPassword to
    // receive a 200 OK token but never resolve because the persistence write
    // hangs. localStorage has no equivalent failure mode. If even localStorage
    // is unavailable (private windows, storage disabled), fall back to memory.
    setPersistence(auth, browserLocalPersistence).catch(() => {
      console.warn('[auth] localStorage persistence unavailable, falling back to in-memory');
      setPersistence(auth, inMemoryPersistence).catch(() => {});
    });
  } else {
    console.warn('Firebase not initialized - missing configuration');
  }
} catch (error) {
  console.error('Failed to initialize Firebase:', error);
}

export { db, auth };
// Kept for backwards-compat imports that haven't been cleaned up yet.
export const realtimeDb = null;

// Connection management
export const enableOfflineMode = async () => {
  if (!db) return;
  try {
    await disableNetwork(db);
    console.log('Offline mode enabled');
  } catch (error) {
    console.error('Error enabling offline mode:', error);
  }
};

export const enableOnlineMode = async () => {
  if (!db) return;
  try {
    await enableNetwork(db);
    console.log('Online mode enabled');
  } catch (error) {
    console.error('Error enabling online mode:', error);
  }
};

// Check connection status
export const checkConnectionStatus = () => {
  return navigator.onLine && db !== undefined;
};

export default app;